import { useCallback } from 'react';
import {
    CHAT_BLK_TOGGLE_EVENT_OPEN,
    USED_CHAT_BLK_LOGIN_ENTRYPOINT,
} from '../constants';
import { ChatBlkQueryService } from '../services';
import {
    clearChatCache,
    getCacheValue,
    getCanvasWidth,
    getUserData,
    setCacheValue,
} from '../utils';
import useChatBlkStore from './useChatBlkStore';

/**
 * Custom hook that provides functions to load, store, and clear cached data in
 * session storage to be used to restore the chat canvas state.
 * @returns {unknown} An object containing the loadCache, storeCache, and clearCache functions.
 */
const useCache = (): {
    cachedPanelWidth: () => number;
    loadCache: () => Promise<void>;
    storeCache: (
        items:
            | Array<{
                  key: string;
                  value: string;
              }>
            | {
                  key: string;
                  value: string;
              }
    ) => Promise<void>;
    clearCache: (keys: string[] | string) => Promise<void>;
} => {
    const { activeContent } = useChatBlkStore();
    const { isLoggedIn, username } = ChatBlkQueryService.getInstance();

    const cachedPanelWidth = useCallback(() => {
        let panelWidth = getCanvasWidth(window.innerWidth);
        const isLoggedIn = activeContent !== 'login';
        if (isLoggedIn) {
            const currentUserId = username;
            const settings = getUserData(currentUserId, 'settings');
            panelWidth = settings?.panelWidth ?? panelWidth;
        }
        return panelWidth;
    }, [activeContent, username]);

    /**
     * Loads the cached data if the user is logged in and the chat canvas
     * was previously opened with a related question or summarize prompt and logging in
     */
    const loadCache = useCallback(async (): Promise<void> => {
        // Check if the user is logged in and if the chat canvas was previously opened
        // with via canvas login rather that through other methods of logging in
        // that are not related to the ChatBLK.
        if (isLoggedIn) {
            if (getCacheValue(USED_CHAT_BLK_LOGIN_ENTRYPOINT)) {
                getCacheValue('action') === CHAT_BLK_TOGGLE_EVENT_OPEN
                    ? open()
                    : close();
            }

            clearChatCache([
                'action',
                'relatedQuestion',
                'relatedQuestionId',
                'summarize',
                USED_CHAT_BLK_LOGIN_ENTRYPOINT,
            ]);
        }
    }, [isLoggedIn]);

    /**
     * Stores the given items in the cache.
     *
     * @param items - An array of objects containing key-value pairs to be stored in the cache.
     */
    const storeCache = async (
        items: Array<{ key: string; value: string }>
    ): Promise<void> => {
        items.forEach(({ key, value }) => {
            setCacheValue(key, value);
        });
    };

    /**
     * Clears the cache for the specified keys.
     *
     * @param keys - An array of keys to be cleared from the cache.
     */
    const clearCache = useCallback(async (keys: string[]): Promise<void> => {
        clearChatCache(keys);
    }, []);

    return {
        cachedPanelWidth,
        loadCache,
        storeCache,
        clearCache,
    };
};

export default useCache;
